/* Shared guide renderers for Fresher Launchpad, Career Switch & Growth and
   Internship Pathways (content v4.1.0). Uses the pages' existing card/pill
   styling plus the preview-ui Accordion. Generic renderer guarantees no
   nested section is silently dropped. */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, MapPin, X } from 'lucide-react';
import { Accordion } from '../preview-ui/PreviewPrimitives';

const human = (k) => String(k).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const isUrl = (v) => typeof v === 'string' && /^https?:\/\//.test(v);
const isSourceIdKey = (k) => /^source_ids?$/.test(k);

export const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-teal-700 hover:text-teal-900 break-all">{children || href}</a>
);

const SourceRefs = ({ ids, reg }) => {
  const items = (ids || []).map((i) => reg?.[i]).filter(Boolean);
  if (!items.length) return null;
  return (
    <p className="text-xs text-gray-500 mt-1">Sources: {items.map((s, i) => <span key={s.id}>{i > 0 && ' · '}<Ext href={s.url}>{s.title}</Ext></span>)}</p>
  );
};

/* Generic value renderer — strings, urls, lists, dicts (recursive). */
export const Value = ({ v, reg, depth = 0 }) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return <span>{v ? 'Yes' : 'No'}</span>;
  if (isUrl(v)) return <Ext href={v} />;
  if (typeof v !== 'object') return <span>{String(v)}</span>;
  if (Array.isArray(v)) {
    if (!v.length) return null;
    if (v.every((x) => typeof x !== 'object')) return <ul className="list-disc pl-5 space-y-1">{v.map((x, i) => <li key={i}>{isUrl(x) ? <Ext href={x} /> : String(x)}</li>)}</ul>;
    return (
      <div className={`grid gap-2 ${depth === 0 ? 'sm:grid-cols-2' : ''}`}>
        {v.map((x, i) => (
          <div key={i} className="bg-white border border-teal-100 rounded-2xl px-4 py-3 text-sm">
            {Value({ v: x, reg, depth: depth + 1 })}
          </div>
        ))}
      </div>
    );
  }
  if (v.url && v.label) return <p><Ext href={v.url}>{v.label}</Ext>{v.text && <span> — {v.text}</span>}<SourceRefs ids={v.source_ids} reg={reg} /></p>;
  return (
    <div className="space-y-1.5">
      {Object.entries(v).map(([k, x]) => {
        if (isSourceIdKey(k)) return <SourceRefs key={k} ids={x} reg={reg} />;
        if (x === null || x === undefined || x === '' || (Array.isArray(x) && !x.length)) return null;
        const simple = typeof x !== 'object';
        return (
          <div key={k}>
            <span className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700">{human(k)}</span>
            {simple ? <span className="text-gray-700"> — {Value({ v: x, reg, depth: depth + 1 })}</span> : <div className="mt-1 text-gray-700">{Value({ v: x, reg, depth: depth + 1 })}</div>}
          </div>
        );
      })}
    </div>
  );
};

/* 36 states / UTs — selector + guidance card. */
export const StateGuide = ({ coverage, reg, tid }) => {
  const [code, setCode] = useState('');
  if (!coverage?.jurisdictions?.length) return null;
  const j = coverage.jurisdictions.find((x) => x.code === code);
  return (
    <div data-testid={`${tid}-state-guide`}>
      {coverage.coverage_type && <p className="text-sm text-gray-600 mb-3">{coverage.coverage_type}</p>}
      {coverage.how_to_use?.length > 0 && <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1 mb-3">{coverage.how_to_use.map((x, i) => <li key={i}>{x}</li>)}</ul>}
      {coverage.important_distinction && <p className="text-[13px] text-amber-900 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-4">{coverage.important_distinction}</p>}
      <label className="block text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1.5" htmlFor={`${tid}-state-select`}>Choose your state or union territory ({coverage.jurisdiction_count || coverage.jurisdictions.length})</label>
      <select id={`${tid}-state-select`} value={code} onChange={(e) => setCode(e.target.value)} data-testid={`${tid}-state-select`}
        className="w-full max-w-xl min-h-[48px] px-4 rounded-full bg-white border-2 border-teal-100 focus:border-teal-500 outline-none text-[15px] text-gray-900">
        <option value="">Select…</option>
        {coverage.jurisdictions.map((x) => <option key={x.code} value={x.code}>{x.name}{x.kind === 'union_territory' ? ' (UT)' : ''}</option>)}
      </select>
      {j && (
        <div className="mt-4 bg-white border-2 border-teal-100 rounded-3xl p-5 space-y-3" data-testid={`${tid}-state-card`}>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2"><MapPin size={16} className="text-teal-600" /> {j.name}</h3>
            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-600">
              {j.current_openings_verified ? 'Openings checked' : 'Current openings not verified'}
            </span>
          </div>
          {j.guidance && <p className="text-[15px] text-gray-700 leading-relaxed">{j.guidance}</p>}
          {j.official_destinations?.length > 0 && (
            <div><p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1">Official destinations</p>
              <ul className="space-y-1 text-sm">{j.official_destinations.map((d, i) => <li key={i}><Ext href={d.url}>{d.label}</Ext>{d.note && <span className="text-gray-600"> — {d.note}</span>}</li>)}</ul></div>
          )}
          {j.local_checks?.length > 0 && <div><p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1">Local checks</p><ul className="list-disc pl-5 text-sm space-y-1">{j.local_checks.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          {j.access_limitations?.length > 0 && <div><p className="text-[11px] uppercase tracking-widest font-extrabold text-amber-800 mb-1">Access limitations</p><ul className="list-disc pl-5 text-sm space-y-1 text-gray-600">{j.access_limitations.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          <SourceRefs ids={j.source_ids} reg={reg} />
        </div>
      )}
    </div>
  );
};

/* Internal links resolved by target → existing route; unresolved never rendered. */
export const RouteLinks = ({ links, tid }) => {
  const ok = (links || []).filter((l) => l.route);
  if (!ok.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${tid}-route-links`}>
      {ok.map((l) => (
        <Link key={l.target} to={l.route} data-testid={`${tid}-route-${l.target}`}
          className="inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 rounded-full border-2 border-teal-600 text-teal-700 hover:bg-teal-50 font-bold text-[13px]">
          {l.label} <ArrowRight size={13} />
        </Link>
      ))}
    </div>
  );
};

/* All guide modules as accordions. `modules` = [{id,title,body}] */
export const GuideAccordions = ({ modules, tid }) => (
  <div className="space-y-3" data-testid={`${tid}-guides`}>
    {modules.filter((m) => m && m.body).map((m) => (
      <Accordion key={m.id} title={m.title} defaultOpen={!!m.open} testId={`${tid}-guide-${m.id}`}>
        <div className="text-[15px] text-gray-700 leading-relaxed">{m.body}</div>
      </Accordion>
    ))}
  </div>
);

/* Details modal — same structure/styling as InternshipPathfinder's DetailsModal. */
export const GuideModal = ({ title, badge, onClose, children, footer, tid }) => {
  const closeRef = useRef(null);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid={`${tid}-details-modal`}>
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-label={`${title} details`} className="relative bg-white w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 md:px-7 pt-5 pb-4 border-b border-gray-100">
          <div>
            {badge && <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-200 mb-2">{badge}</span>}
            <h3 className="text-lg md:text-xl font-bold text-gray-900">{title}</h3>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close details" data-testid={`${tid}-details-close`}
            className="shrink-0 w-11 h-11 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 md:px-7 py-5 space-y-5">{children}</div>
        {footer && <div className="px-5 md:px-7 py-4 border-t border-gray-100 bg-gray-50/70">{footer}</div>}
      </div>
    </div>
  );
};

export const Row = ({ label, value, reg }) => (value === null || value === undefined || value === '' ? null : (
  <div>
    <p className="text-[11px] uppercase tracking-widest font-extrabold text-teal-700 mb-1">{label}</p>
    <div className="text-[15px] text-gray-700 leading-relaxed">{Value({ v: value, reg })}</div>
  </div>
));

export const ExtBtn = ({ href, label, tid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={tid}
    className="inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-[13px] transition-colors">
    {label} <ExternalLink size={13} aria-hidden="true" />
  </a>
);
